import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './SplashScreen.css';

const SplashScreen = ({ onComplete }) => {
    const [phase, setPhase] = useState(0);
    const [counter, setCounter] = useState(0);

    useEffect(() => {
        const timers = [
            setTimeout(() => setPhase(1), 200),
            setTimeout(() => setPhase(2), 800),
            setTimeout(() => setPhase(3), 2000),
            setTimeout(() => setPhase(4), 3500),
            setTimeout(() => setPhase(5), 4300),
            setTimeout(() => setPhase(6), 5000),
        ];
        return () => timers.forEach(clearTimeout);
    }, []);

    // Lightweight counter
    useEffect(() => {
        if (phase < 1) return;
        const target = phase >= 5 ? 100 : Math.min(phase * 22, 95);
        const id = setInterval(() => {
            setCounter(prev => {
                if (prev >= target) { clearInterval(id); return prev; }
                return prev + 1;
            });
        }, 40);
        return () => clearInterval(id);
    }, [phase]);

    useEffect(() => {
        if (phase === 6) onComplete();
    }, [phase, onComplete]);

    return (
        <AnimatePresence>
            {phase < 6 && (
                <motion.div className="splash-screen" exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
                    <div className="splash-bg" />

                    {/* Only 6 lightweight particles */}
                    <div className="splash-particles">
                        {Array.from({ length: 6 }).map((_, i) => (
                            <div key={i} className="splash-particle" />
                        ))}
                    </div>

                    <div className="splash-content">
                        <motion.p
                            className="splash-greeting"
                            initial={{ opacity: 0 }}
                            animate={phase >= 1 ? { opacity: 1 } : {}}
                            transition={{ duration: 0.4 }}
                        >
                            Welcome to my portfolio
                        </motion.p>

                        <motion.div
                            className="splash-line"
                            initial={{ scaleX: 0 }}
                            animate={phase >= 2 ? { scaleX: 1 } : {}}
                            transition={{ duration: 0.5 }}
                        />

                        <motion.h1
                            className="splash-name"
                            initial={{ opacity: 0, y: 20 }}
                            animate={phase >= 2 ? { opacity: 1, y: 0 } : {}}
                            transition={{ duration: 0.6 }}
                        >
                            Lakshmanan <span className="accent">R</span>
                        </motion.h1>

                        <motion.p
                            className="splash-tagline-simple"
                            initial={{ opacity: 0 }}
                            animate={phase >= 3 ? { opacity: 1 } : {}}
                            transition={{ duration: 0.4 }}
                        >
                            DEVELOPER • INNOVATOR • CREATOR
                        </motion.p>

                        <motion.div
                            className="splash-line"
                            initial={{ scaleX: 0 }}
                            animate={phase >= 3 ? { scaleX: 1 } : {}}
                            transition={{ duration: 0.5 }}
                        />

                        <motion.p
                            className="splash-ready"
                            initial={{ opacity: 0 }}
                            animate={phase >= 4 ? { opacity: 1 } : {}}
                            transition={{ duration: 0.3 }}
                        >
                            {phase >= 5 ? '✓ Ready' : 'Loading...'}
                        </motion.p>
                    </div>

                    {/* Loading bar */}
                    <div className="splash-loader">
                        <div className="splash-loader-fill" style={{ width: `${counter}%` }} />
                        <span className="splash-loader-text">{counter}%</span>
                    </div>

                    {/* Curtains */}
                    <motion.div
                        className="curtain-left"
                        initial={{ x: 0 }}
                        animate={phase >= 5 ? { x: '-100%' } : {}}
                        transition={{ duration: 0.6, ease: [0.76, 0, 0.24, 1] }}
                    />
                    <motion.div
                        className="curtain-right"
                        initial={{ x: 0 }}
                        animate={phase >= 5 ? { x: '100%' } : {}}
                        transition={{ duration: 0.6, ease: [0.76, 0, 0.24, 1] }}
                    />
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default SplashScreen;
